import React, {ReactElement} from "react";
import {Button, ButtonProps, IconButton, Tooltip} from "@mui/material";
import {
  AnimationOutlined,
  AutoFixHighOutlined,
  ContentCopyOutlined,
  DeleteOutlined,
  FlipOutlined,
  GroupWorkOutlined,
  OpacityOutlined,
  RedoOutlined,
  TuneOutlined,
  UndoOutlined,
  WorkspacesOutlined,
} from "@mui/icons-material";
import {DIVIDER, PD_SM, PD_XSM, SZ_SM} from "../../shared/dimens";
import {CanvasElement, isGroupElement, ShapeCanvasElement} from "./types";
import {StyledVerticalDivider} from "../../shared/StyledComponents";

function ColorSwatch({color}: { color: string }): ReactElement {
  return (
    <div style={{
      width: 20,
      height: 20,
      borderRadius: 4,
      background: color,
      border: "1px solid #ccc",
    }}/>
  );
}

function ToolbarButton(props: ButtonProps): ReactElement {
  return <Button {...props} style={{width: 40, height: 40, padding: 0, minWidth: 0, ...props.style}}/>;
}

export function StudioToolbar(props: {
  selectedElement: CanvasElement | null;
  selectedCount: number;
  onUndo: () => void;
  onRedo: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onGroup: () => void;
  onUngroup: () => void;
}): ReactElement {
  const {selectedElement, selectedCount, onUndo, onRedo, onDuplicate, onDelete, onGroup, onUngroup} = props;
  const isShape = selectedElement?.type === "shape";
  const isGroup = selectedElement ? isGroupElement(selectedElement) : false;
  const canGroup = selectedCount >= 2;
  const fillColor = isShape ? (selectedElement as ShapeCanvasElement).fill : null;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      padding: `${PD_XSM}px ${PD_SM}px`,
      borderBottom: DIVIDER,
      background: "#fff",
      height: SZ_SM,
    }}>
      <Tooltip title="Undo">
        <ToolbarButton onClick={onUndo}>
          <UndoOutlined fontSize="small"/>
        </ToolbarButton>
      </Tooltip>
      <Tooltip title="Redo">
        <ToolbarButton onClick={onRedo}>
          <RedoOutlined fontSize="small"/>
        </ToolbarButton>
      </Tooltip>

      {canGroup && <>
        <StyledVerticalDivider/>
        <Tooltip title="Group">
          <ToolbarButton onClick={onGroup}>
            <GroupWorkOutlined fontSize="small"/>
          </ToolbarButton>
        </Tooltip>
      </>}

      {isGroup && <>
        <StyledVerticalDivider/>
        <Tooltip title="Ungroup">
          <ToolbarButton onClick={onUngroup}>
            <WorkspacesOutlined fontSize="small"/>
          </ToolbarButton>
        </Tooltip>
      </>}

      {selectedElement && <>
        <StyledVerticalDivider/>

        {fillColor && (
          <Tooltip title="Fill color">
            <ToolbarButton>
              <ColorSwatch color={fillColor}/>
            </ToolbarButton>
          </Tooltip>
        )}

        <Tooltip title="Opacity">
          <ToolbarButton>
            <OpacityOutlined fontSize="small"/>
          </ToolbarButton>
        </Tooltip>

        {isShape && (
          <Tooltip title="Flip">
            <ToolbarButton>
              <FlipOutlined fontSize="small"/>
            </ToolbarButton>
          </Tooltip>
        )}

        <Tooltip title="Effects">
          <Button size="small" startIcon={<AutoFixHighOutlined/>} style={{textTransform: "none"}}>
            Effects
          </Button>
        </Tooltip>

        <Tooltip title="Animate">
          <Button size="small" startIcon={<AnimationOutlined/>} style={{textTransform: "none"}}>
            Animate
          </Button>
        </Tooltip>

        {/* Spacer */}
        <div style={{flexGrow: 1}}/>

        {/* Right: element actions */}
        <Tooltip title="Duplicate">
          <ToolbarButton onClick={onDuplicate}>
            <ContentCopyOutlined fontSize="small"/>
          </ToolbarButton>
        </Tooltip>
        <Tooltip title="Delete">
          <ToolbarButton onClick={onDelete}>
            <DeleteOutlined fontSize="small"/>
          </ToolbarButton>
        </Tooltip>

        <StyledVerticalDivider/>

        <Tooltip title="Position">
          <Button startIcon={<TuneOutlined/>} style={{textTransform: "none"}}>
            Position
          </Button>
        </Tooltip>
      </>}
    </div>
  );
}
